import { useAuthStore } from '@/stores/useAuthStore'
import { recoverSession } from '@/auth/recoverSession'
import { gotoLogin } from '@/auth/gotoLogin'

const SUITE_URL = import.meta.env.VITE_SUITE_URL as string

/**
 * Endpoints that ARE the recovery primitives. Retrying them on 401 would
 * recurse into recoverSession, which holds the lock — re-entrant deadlock.
 */
const AUTH_ENDPOINTS = [
  '/auth/user/login',
  '/auth/user/logout',
  '/auth/user/refresh',
  '/auth/user/login-password',
]

function isAuthEndpoint(urlOrPath: string): boolean {
  return AUTH_ENDPOINTS.some((e) => urlOrPath.includes(e))
}

function isMutating(method: string): boolean {
  const m = method.toUpperCase()
  return m === 'POST' || m === 'PUT' || m === 'PATCH' || m === 'DELETE'
}

function resolveUrl(input: RequestInfo | URL | string): string {
  if (typeof input === 'string') {
    if (input.startsWith('http')) return input
    return `${SUITE_URL}${input.startsWith('/') ? '' : '/'}${input}`
  }
  if (input instanceof URL) return input.href
  return input.url
}

/**
 * Single fetch entry point for boostcore API calls.
 *
 * - Adds csrf header on mutations only
 * - Always sends credentials (cookie auth)
 * - On 401 (and only on non-auth endpoints), runs recoverSession() once and
 *   retries. If recovery fails, hands the user to gotoLogin() and returns the
 *   raw 401 response to the caller (caller is expected to bail).
 *
 * Accepts either a relative path ("/auth/user/me") or absolute URL — same
 * shape as native fetch, so it can also serve as the swagger fetchApi.
 */
export async function apiFetch(
  input: RequestInfo | URL | string,
  init: RequestInit = {},
  isRetry = false,
): Promise<Response> {
  const url = resolveUrl(input)
  const method = (init.method ?? 'GET').toUpperCase()
  const headers = new Headers(init.headers ?? {})

  // CSRF: only on state-changing methods. Strip it from safe methods so
  // auto-generated swagger code that stamps it indiscriminately doesn't
  // pollute server logs / network panel.
  if (isMutating(method)) {
    const csrf = useAuthStore().getCoreCsrfToken()
    if (csrf) headers.set('x-csrf-token', csrf)
  } else {
    headers.delete('x-csrf-token')
  }

  const finalInit: RequestInit = {
    ...init,
    method,
    headers,
    credentials: init.credentials ?? 'include',
  }

  let res: Response
  try {
    res = await fetch(url, finalInit)
  } catch (err) {
    if (!isRetry && navigator.onLine) {
      await new Promise((r) => setTimeout(r, 1_000))
      return apiFetch(input, init, true)
    }
    throw err
  }

  if (res.status !== 401 || isRetry || isAuthEndpoint(url)) return res

  const recovered = await recoverSession()
  if (recovered) return apiFetch(input, init, true)

  // No path forward — push the user to /auth/login. We still return the 401
  // so callers stop processing (most stores check res.ok and bail).
  gotoLogin()
  return res
}
